import { useEffect, useState } from "react";
import { type BingoPublicState } from "@hitster/shared";
import { useGame } from "../hooks/useGameContext";
import { BingoGrid } from "./BingoGrid";
import { BingoAnswerPanel } from "./BingoAnswerPanel";
import { BingoCategoryReveal } from "./BingoCategoryReveal";
import { QuitGameModal } from "./QuitGameModal";
import { GameModal } from "./GameModal";

export function BingoGamePage() {
  const { game, room, playerId, emitUserAction, leaveLobby, audioLoadFailed, audioLoading } = useGame();
  const [, tick] = useState(0);
  const [quitOpen, setQuitOpen] = useState(false);
  const [overDismissed, setOverDismissed] = useState(false);

  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 250);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (game?.phase !== "GAME_OVER") setOverDismissed(false);
  }, [game?.phase]);

  if (!game || !room || !game.bingo) {
    return <div className="text-center py-20">Chargement du Bingo…</div>;
  }

  const bingo: BingoPublicState = game.bingo;
  const me = game.players.find((p) => p.id === playerId);
  const isHost = playerId === room.hostId;
  const isSpectator = me?.role === "spectator";
  const myGrid = bingo.grids.find((g) => g.playerId === playerId);
  const others = bingo.grids.filter((g) => g.playerId !== playerId && !g.hasLeft);
  const cat = bingo.category;
  const playingCount = game.players.filter((p) => p.role === "player" && !p.hasLeft).length;
  const deadline = bingo.answerDeadline;
  const timedEnd =
    bingo.phase === "PLAYING"
      ? deadline
      : bingo.phase === "CELL_SELECTION" ||
          bingo.phase === "POWER_SELECTION" ||
          bingo.phase === "ANSWER_VOTE"
        ? bingo.phaseEndsAt
        : null;
  const remaining = timedEnd ? Math.max(0, Math.ceil((timedEnd - Date.now()) / 1000)) : null;
  const winners = bingo.winners;
  const winnerNames = winners.map((w) => w.nickname);
  const myWin = winners.find((w) => w.playerId === playerId);
  const canUsePower = bingo.phase === "POWER_SELECTION" && bingo.pendingPowerPlayerIds.includes(playerId ?? "");
  const powerNames = bingo.pendingPowerPlayerIds
    .map((id) => bingo.grids.find((g) => g.playerId === id)?.nickname)
    .filter((n): n is string => Boolean(n));
  const stealByPlayer = new Map(bingo.powerTargets.map((t) => [t.playerId, t.cells.map((c) => c.index)]));

  const submit = (answer: string | number) => {
    if (!bingo.roundId) return;
    emitUserAction("bingo:submit-answer", { roundId: bingo.roundId, answer });
  };

  const selectCell = (cellIndex: number) => {
    if (!bingo.roundId) return;
    emitUserAction("bingo:select-cell", { roundId: bingo.roundId, cellIndex });
  };

  const usePower = (targetPlayerId: string, cellIndex: number) => {
    if (!bingo.roundId) return;
    emitUserAction("bingo:use-power", { roundId: bingo.roundId, targetPlayerId, cellIndex });
  };

  const retryAudio = () => {
    if (bingo.roundId) emitUserAction("audio:request-play", { roundId: bingo.roundId });
  };

  return (
    <div className="-mx-4 px-3 sm:px-4 md:-mx-8 md:px-8 w-[calc(100%+2rem)] md:w-[calc(100%+4rem)] space-y-3 sm:space-y-5 pb-8">
      <header className="cw-panel p-3 sm:p-4 flex flex-wrap items-center justify-between gap-2">
        <div>
          <p className="text-xs sm:text-sm text-violet-400">
            {bingo.phase === "GAME_OVER" ? "Partie terminée" : `Manche ${bingo.roundNumber}`}
          </p>
          {cat && bingo.phase !== "CATEGORY_REVEAL" && (
            <p className="text-lg sm:text-xl font-bold" style={{ color: cat.color }}>
              {cat.icon} {cat.name}
            </p>
          )}
        </div>
        <div className="flex items-center gap-2">
          {remaining !== null && (
            <span className="tabular-nums text-2xl sm:text-3xl font-extrabold text-orange-400 min-w-[3.5rem] text-center">
              {String(Math.floor(remaining / 60)).padStart(2, "0")}:{String(remaining % 60).padStart(2, "0")}
            </span>
          )}
          {game.phase !== "GAME_OVER" && (
            <button type="button" className="cw-btn cw-btn-secondary cw-btn-game" onClick={() => setQuitOpen(true)}>
              Quitter
            </button>
          )}
        </div>
        {others.length > 0 && bingo.phase !== "CATEGORY_REVEAL" && (
          <div className="w-full flex flex-wrap gap-2 pt-2 border-t border-white/10">
            {others.map((g) => (
              <span
                key={g.playerId}
                className="inline-flex items-center gap-1.5 rounded-full bg-white/5 px-2.5 py-1 text-xs sm:text-sm"
              >
                <span className="text-violet-100">{g.nickname}</span>
                <span className="tabular-nums text-orange-300 font-semibold">
                  {g.checkedCount}/{g.size * g.size}
                </span>
              </span>
            ))}
          </div>
        )}
      </header>

      {bingo.phase === "CATEGORY_REVEAL" && cat && (
        <BingoCategoryReveal category={cat} roundNumber={bingo.roundNumber} />
      )}

      {(bingo.phase === "PLAYING" || bingo.phase === "ANSWERS_LOCKED") && (
        <>
          <div className="cw-panel p-4 flex items-center justify-center gap-3">
            <div className="flex gap-1 h-8 items-end" aria-hidden>
              {[0, 1, 2, 3, 4, 5].map((i) => (
                <div
                  key={i}
                  className="w-1.5 bg-violet-400 rounded wave-pulse"
                  style={{ animationDelay: `${i * 0.12}s`, height: `${10 + i * 4}px` }}
                />
              ))}
            </div>
            <p className="text-violet-200">♪ Chanson en cours</p>
            {(audioLoading || audioLoadFailed) && (
              <button type="button" className="cw-btn cw-btn-secondary cw-btn-game" onClick={retryAudio}>
                {audioLoadFailed ? "Réessayer l'extrait" : "Chargement…"}
              </button>
            )}
          </div>
          {!isSpectator && bingo.phase === "PLAYING" && (
            <BingoAnswerPanel bingo={bingo} disabled={false} onSubmit={submit} />
          )}
          {bingo.phase === "ANSWERS_LOCKED" && (
            <p className="text-center text-violet-300">Réponses verrouillées…</p>
          )}
          <p className="text-center text-sm text-violet-400">
            {bingo.answeredCount} / {bingo.answeringTotal} joueurs ont répondu
          </p>
        </>
      )}

      {bingo.reveal &&
        (bingo.phase === "ANSWER_VOTE" ||
          bingo.phase === "RESULT_REVEAL" ||
          bingo.phase === "CELL_SELECTION" ||
          bingo.phase === "ROUND_END") && (
          <div className="cw-panel p-5 sm:p-8 text-center space-y-3 reveal-pop">
            <p className="text-sm text-violet-400">🎵 Révélation</p>
            <h2 className="cw-title text-3xl sm:text-5xl font-extrabold">{bingo.reveal.song.title}</h2>
            <p className="text-xl text-violet-200">{bingo.reveal.song.artist}</p>
            <p className="text-4xl font-extrabold text-orange-400">📅 {bingo.reveal.song.releaseYear}</p>
            <p className="text-lg">
              Bonne réponse : <strong style={{ color: cat?.color }}>{bingo.reveal.correctLabel}</strong>
            </p>
            {bingo.phase === "ANSWER_VOTE" && (
              <p className="text-orange-300 font-semibold">
                Une saisie a été refusée — validez-la si c’est assez proche (10 s).
              </p>
            )}
            <ul className="text-left max-w-md mx-auto space-y-2">
              {bingo.reveal.answers.map((a) => {
                const voteItem = bingo.answerVote?.items.find((i) => i.playerId === a.playerId);
                const canVote =
                  bingo.phase === "ANSWER_VOTE" &&
                  !isSpectator &&
                  voteItem &&
                  a.playerId !== playerId &&
                  !voteItem.myVoted &&
                  !voteItem.majority;
                return (
                  <li key={a.playerId} className="flex flex-wrap items-center justify-between gap-2 text-sm sm:text-base">
                    <span>{a.nickname}</span>
                    <span className="flex items-center gap-2">
                      <span
                        className={
                          a.correct || voteItem?.majority ? "text-green-400" : "text-red-400"
                        }
                      >
                        {a.correct || voteItem?.majority ? "✅" : "❌"} {a.answerLabel ?? "—"}
                        {a.exactYearBonus ? " ⭐" : ""}
                        {a.validatedByVote ? " (validé par le vote)" : ""}
                        {voteItem && !a.correct ? ` · ${voteItem.voteCount}/${voteItem.needed}` : ""}
                      </span>
                      {canVote && (
                        <button
                          type="button"
                          className="cw-btn cw-btn-primary text-xs py-1 px-2"
                          onClick={() =>
                            bingo.roundId &&
                            emitUserAction("bingo:vote-answer", {
                              roundId: bingo.roundId,
                              targetPlayerId: a.playerId,
                            })
                          }
                        >
                          Je valide
                        </button>
                      )}
                    </span>
                  </li>
                );
              })}
            </ul>
          </div>
        )}

      {bingo.phase === "CELL_SELECTION" && myGrid && !isSpectator && (
        <div className="space-y-3">
          <p className="text-center font-semibold">
            {bingo.selectableCellIndexes.length
              ? "Choisissez une case à valider"
              : "En attente des autres joueurs…"}
          </p>
          <BingoGrid
            cells={myGrid.cells}
            size={myGrid.size}
            selectableIndexes={bingo.selectableCellIndexes}
            onSelect={selectCell}
          />
        </div>
      )}

      {bingo.phase === "POWER_SELECTION" && (
        <div className="cw-panel p-4 sm:p-5 space-y-4 border-2 border-orange-400/80">
          <p className="text-center text-2xl sm:text-3xl font-extrabold text-orange-400">⭐ Année pile !</p>
          <p className="text-center text-violet-100">
            {powerNames.length > 1
              ? `${powerNames.join(" et ")} peuvent chacun retirer une case à un adversaire.`
              : `${powerNames[0] ?? "Un joueur"} peut retirer une case à un adversaire.`}
          </p>
          {!isSpectator && canUsePower ? (
            bingo.powerTargets.length === 0 ? (
              <button
                type="button"
                className="cw-btn cw-btn-secondary w-full"
                onClick={() => bingo.roundId && emitUserAction("bingo:skip-power", { roundId: bingo.roundId })}
              >
                Aucune case à retirer — passer
              </button>
            ) : (
              <div className="space-y-4">
                <p className="text-center font-semibold text-orange-200">
                  Touchez la case précise sur la grille adverse — pas le type, l’emplacement.
                </p>
                {others.map((g) => {
                  const stealIndexes = stealByPlayer.get(g.playerId) ?? [];
                  return (
                    <div key={g.playerId} className="space-y-2">
                      <p className="text-center text-sm text-violet-300">Grille de {g.nickname}</p>
                      <BingoGrid
                        cells={g.cells}
                        size={g.size}
                        selectableIndexes={stealIndexes}
                        steal
                        onSelect={(index) => usePower(g.playerId, index)}
                      />
                    </div>
                  );
                })}
                <button
                  type="button"
                  className="cw-btn cw-btn-secondary w-full"
                  onClick={() => bingo.roundId && emitUserAction("bingo:skip-power", { roundId: bingo.roundId })}
                >
                  Ne pas retirer de case
                </button>
              </div>
            )
          ) : (
            <div className="space-y-4">
              <p className="text-center text-violet-300">
                {powerNames.length
                  ? `En attente : ${powerNames.join(", ")}…`
                  : "En attente des pouvoirs…"}
              </p>
              {others.map((g) => (
                <div key={g.playerId} className="space-y-2">
                  <p className="text-center text-sm text-violet-300">Grille de {g.nickname}</p>
                  <BingoGrid cells={g.cells} size={g.size} compact />
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {myGrid && bingo.phase !== "CATEGORY_REVEAL" && bingo.phase !== "CELL_SELECTION" && (
        <div className="space-y-2">
          <p className="text-sm text-violet-400 text-center">Votre grille</p>
          <BingoGrid cells={myGrid.cells} size={myGrid.size} highlightLine={myWin?.lineIndexes} />
        </div>
      )}

      {others.length > 0 && bingo.phase !== "CATEGORY_REVEAL" && bingo.phase !== "POWER_SELECTION" && (
        <div className="cw-panel p-3 sm:p-4 space-y-3">
          <p className="text-sm text-violet-400">Adversaires</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {others.map((g) => {
              const total = g.size * g.size;
              const pct = Math.round((g.checkedCount / total) * 100);
              return (
                <div key={g.playerId} className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span>
                      {g.nickname}
                      {!g.connected && <span className="text-red-400 ml-2">hors ligne</span>}
                    </span>
                    <span className="tabular-nums text-violet-300">
                      {g.checkedCount}/{total}
                    </span>
                  </div>
                  <div className="h-2 rounded-full bg-white/10 overflow-hidden">
                    <div className="h-full bg-gradient-to-r from-violet-500 to-orange-400" style={{ width: `${pct}%` }} />
                  </div>
                  <BingoGrid cells={g.cells} size={g.size} compact />
                </div>
              );
            })}
          </div>
        </div>
      )}

      <GameModal
        open={bingo.phase === "GAME_OVER" && !overDismissed}
        onClose={() => setOverDismissed(true)}
        labelledBy="bingo-over-title"
      >
        <p className="text-5xl" aria-hidden>
          🎉
        </p>
        <h2 id="bingo-over-title" className="cw-title text-4xl font-bold text-orange-400 bingo-win-pop">
          BINGO !
        </h2>
        <p className="text-xl">
          {winnerNames.length === 0
            ? "Partie terminée"
            : winnerNames.length === 1
              ? `🏆 ${winnerNames[0]} remporte la partie !`
              : `🏆 ${winnerNames.join(" et ")} remportent la partie !`}
        </p>
        {myGrid && (
          <BingoGrid cells={myGrid.cells} size={myGrid.size} highlightLine={myWin?.lineIndexes} compact />
        )}
        {bingo.stats[playerId ?? ""] && (
          <p className="text-sm text-violet-300">
            {bingo.stats[playerId!].correct} bonnes · {bingo.stats[playerId!].wrong} mauvaises
            {bingo.stats[playerId!].exactYears ? ` · ${bingo.stats[playerId!].exactYears} année(s) exacte(s)` : ""}
          </p>
        )}
        <button type="button" className="cw-btn cw-btn-primary w-full" onClick={() => setOverDismissed(true)}>
          Voir le récapitulatif
        </button>
      </GameModal>

      {bingo.phase === "GAME_OVER" && overDismissed && (
        <div className="sticky bottom-4 z-40 cw-panel p-4 space-y-3 border border-orange-500/30">
          <div className="space-y-1 text-sm">
            {bingo.grids.map((g) => {
              const st = bingo.stats[g.playerId];
              const total = (st?.correct ?? 0) + (st?.wrong ?? 0);
              const pct = total ? Math.round(((st?.correct ?? 0) / total) * 100) : 0;
              return (
                <p key={g.playerId}>
                  <strong>{g.nickname}</strong> — {st?.correct ?? 0} bonnes, {st?.wrong ?? 0} mauvaises ({pct}%)
                  {st?.exactYears ? ` · ${st.exactYears} ★` : ""}
                </p>
              );
            })}
          </div>
          {isHost ? (
            <div className="flex flex-wrap gap-2">
              <button type="button" className="cw-btn cw-btn-primary flex-1" onClick={() => emitUserAction("game:rematch")}>
                Revanche
              </button>
              <button type="button" className="cw-btn cw-btn-secondary flex-1" onClick={() => emitUserAction("game:restart")}>
                Retour au lobby
              </button>
            </div>
          ) : (
            <p className="text-sm text-violet-400">En attente de l&apos;hôte…</p>
          )}
        </div>
      )}

      <QuitGameModal
        open={quitOpen}
        solo={playingCount <= 1}
        onConfirm={() => {
          setQuitOpen(false);
          leaveLobby();
        }}
        onClose={() => setQuitOpen(false)}
      />
    </div>
  );
}
